/**
 * "Estornos recebidos" x "comissao revertida": dois numeros que parecem o
 * mesmo e nao sao (ADR-001 E2).
 *
 * Decisoes nao obvias:
 *  1. Estorno recebido e ESTATISTICA (o lead pediu o dinheiro de volta);
 *     comissao revertida e CONSEQUENCIA (a politica da empresa desfez a
 *     comissao). Reembolso nao reverte no padrao, entao o primeiro numero
 *     costuma ser bem maior que o segundo -- e a tela explica por que.
 *  2. A tabela por tipo mostra, para cada um, o que foi MANTIDO e o que foi
 *     REVERTIDO, com atalho para o extrato filtrado em cada lado.
 *  3. "E se a politica fosse outra": aplica cada uma das tres politicas aos
 *     MESMOS estornos do recorte e mostra quanto sairia. Marca quantas
 *     empresas usam cada politica hoje (cadastro vigente, `useCompany`).
 *     Nada muda no extrato: estorno ja processado fica com a politica da
 *     epoca, e a nota diz isso.
 */

import { Badge, Button, Footnote, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount } from '../../../lib/format';
import type { CompanySummary, RefundPolicy } from '../../../types';
import { comissaoRevertidaSob, ORDEM_ESTORNOS, ORDEM_POLITICAS, POLITICA_PADRAO } from '../regras';
import type { CommissionSummary, PeriodTotals, RefundFilter } from '../types';
import { ESTORNO, POLITICA_ESTORNO, plural } from '../vocabulario';
import { Delta } from './Delta';

export function EstornosERevertidas({
  summary,
  previous,
  empresas,
  filtroAtivo,
  onVerNoExtrato,
  className = '',
}: {
  readonly summary: CommissionSummary;
  readonly previous: PeriodTotals | null;
  /** Empresas do recorte com o cadastro vigente (para contar a politica de cada uma). */
  readonly empresas: readonly CompanySummary[];
  readonly filtroAtivo: RefundFilter;
  readonly onVerNoExtrato: (filtro: RefundFilter) => void;
  readonly className?: string;
}): JSX.Element {
  const mantidas = ORDEM_ESTORNOS.reduce((acc, tipo) => acc + summary.refunds[tipo].count - summary.refunds[tipo].reversedCount, 0);
  const preservada = ORDEM_ESTORNOS.reduce((acc, tipo) => acc + summary.refunds[tipo].keptCommissionCents, 0);
  const empresasPorPolitica = (politica: RefundPolicy): number => empresas.filter((e) => (e.refundPolicy ?? POLITICA_PADRAO) === politica).length;

  return (
    <SectionCard
      id="estornos"
      title="Estornos recebidos x comissão revertida"
      icon="alerta"
      description="Reembolso não reverte a comissão; chargeback reverte. A política é de cada empresa e fica congelada em cada estorno."
      className={className}
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            aria-pressed={filtroAtivo === 'com_estorno'}
            onClick={() => onVerNoExtrato('com_estorno')}
            className={`flex flex-col gap-1 rounded-xl border px-4 py-3 text-left transition-colors ${filtroAtivo === 'com_estorno' ? 'border-brand bg-brand/5' : 'border-line bg-elevated hover:border-brand/40'}`}
            title="Ver no extrato todas as conversões com estorno recebido"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Estornos recebidos</span>
            <span className="text-2xl font-semibold tracking-tight text-ink">
              {formatCount(summary.refundCount)} <span className="text-sm font-medium text-ink-muted">· {formatBRLFromCents(summary.refundedAmountCents)} devolvidos</span>
            </span>
            <span className="text-xs text-ink-subtle">
              Estatística: {formatCount(mantidas)} com a comissão mantida ({formatBRLFromCents(preservada)})
            </span>
            <Delta atual={summary.refundCount} anterior={previous === null ? null : previous.refundCount} tipo="contagem" sentido="neutro" />
          </button>
          <button
            type="button"
            aria-pressed={filtroAtivo === 'revertida'}
            onClick={() => onVerNoExtrato('revertida')}
            className={`flex flex-col gap-1 rounded-xl border px-4 py-3 text-left transition-colors ${filtroAtivo === 'revertida' ? 'border-brand bg-brand/5' : 'border-status-critical/40 bg-status-critical/5 hover:border-status-critical/70'}`}
            title="Ver no extrato as conversões revertidas"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Comissão revertida</span>
            <span className="text-2xl font-semibold tracking-tight text-ink">
              {formatBRLFromCents(summary.reversedCommissionCents)}{' '}
              <span className="text-sm font-medium text-ink-muted">
                · {formatCount(summary.reversedCount)} {plural(summary.reversedCount, 'conversão', 'conversões')}
              </span>
            </span>
            <span className="text-xs text-ink-subtle">Saiu da apuração porque a política da empresa desfaz aquele tipo de estorno</span>
            <Delta atual={summary.reversedCommissionCents} anterior={previous === null ? null : previous.reversedCommissionCents} tipo="dinheiro" sentido="menor_melhor" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] border-collapse text-sm">
            <caption className="sr-only">Estornos recebidos por tipo, com a comissão mantida e a revertida.</caption>
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
                <th scope="col" className="py-2 pr-3 font-medium">
                  Tipo
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Recebidos
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Devolvido ao lead
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Comissão mantida
                </th>
                <th scope="col" className="py-2 pl-3 text-right font-medium">
                  Comissão revertida
                </th>
              </tr>
            </thead>
            <tbody>
              {ORDEM_ESTORNOS.map((tipo) => {
                const b = summary.refunds[tipo];
                return (
                  <tr key={tipo} className="border-b border-line/60 last:border-0">
                    <th scope="row" className="py-2 pr-3 text-left font-normal text-ink">
                      {ESTORNO[tipo].label}
                      {tipo === 'reembolso_parcial' ? <span className="block text-xs text-ink-subtle">nunca reverte</span> : null}
                    </th>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">{formatCount(b.count)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{formatBRLFromCents(b.refundedAmountCents)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">{formatBRLFromCents(b.keptCommissionCents)}</td>
                    <td className={`py-2 pl-3 text-right tabular-nums ${b.reversedCommissionCents > 0 ? 'font-medium text-status-critical' : 'text-ink-muted'}`}>
                      {formatBRLFromCents(b.reversedCommissionCents)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">E se todos os estornos do recorte seguissem outra política?</h3>
          <ul className="grid gap-2 sm:grid-cols-3">
            {ORDEM_POLITICAS.map((politica) => {
              const sob = comissaoRevertidaSob(politica, summary.refunds);
              const usando = empresasPorPolitica(politica);
              return (
                <li key={politica} className="flex flex-col gap-1 rounded-xl border border-line bg-surface px-3 py-2.5">
                  <span className="text-xs font-semibold text-ink">{POLITICA_ESTORNO[politica].label}</span>
                  <span className="text-lg font-semibold tabular-nums text-ink">
                    {formatBRLFromCents(sob.cents)} <span className="text-xs font-normal text-ink-subtle">revertidos</span>
                  </span>
                  <span className="text-[11px] text-ink-subtle">{POLITICA_ESTORNO[politica].explica}</span>
                  {usando > 0 ? (
                    <Badge tone="info" className="self-start">
                      Em uso por {formatCount(usando)} {plural(usando, 'empresa', 'empresas')}
                    </Badge>
                  ) : (
                    <span className="text-[11px] text-ink-subtle">Nenhuma empresa usa hoje</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Footnote>Estorno já processado fica com a política da época. Mudar a política de uma empresa (Empresas → Comissão e cobrança) vale para os próximos.</Footnote>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant={filtroAtivo === 'mantida' ? 'primary' : 'ghost'} size="sm" icon="filtro" onClick={() => onVerNoExtrato('mantida')} aria-pressed={filtroAtivo === 'mantida'}>
              Só as mantidas
            </Button>
            {filtroAtivo === 'todos' ? null : (
              <Button variant="ghost" size="sm" icon="x" onClick={() => onVerNoExtrato('todos')}>
                Tirar filtro de estorno
              </Button>
            )}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
